<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useCrmMetrics } from '@/views/CRM/composables/useCrmMetrics';
import { type EmitsToListeners } from '@/types/core';
import type { GetCrmMiscMetricsResponses } from '@/types/openapi-ts';
//#endregion

//#region types
type CrmMetricItem = GetCrmMiscMetricsResponses[200]['payload'][number];

interface Props {
  selectedMetrics?: string[];
}

interface Emits {
  select: [metrics: string[]];
  close: [];
}

export interface ActivityMetricsParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  selectedMetrics: () => [],
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const { crmMetricGroups, metricsIndex } = useCrmMetrics();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);

const selectedMetricsIndex = ref<Record<string, boolean>>({});
const search = ref('');
//#endregion

//#region computed
const pSearch = computed(() => (search.value || '').trim().toLowerCase());
const selectedMetricsList = computed(() => {
  const metrics = Object.keys(selectedMetricsIndex.value);
  return metrics.filter((metric) => selectedMetricsIndex.value[metric]);
});

const columnGroup = computed(() => {
  return crmMetricGroups.value.map((group: any) => ({
    key: `group_${group.group_name}`,
    title: '',
    searchString: '',
    group_name: group.group_name,
    items: group.items.map((metric: any) => {
      const metricMeta: Record<string, any> = metricsIndex.value[metric.name] || {};
      return {
        key: `metric_${metric.name}`,
        name: metric.name,
        ...metricMeta,
        searchString: [
          (metricMeta.title || '').toLowerCase(),
          (metricMeta.description || '').toLowerCase(),
        ].join(' '),
        items: metric.items.map((submetric: any) => {
          const subMetricMeta: Record<string, any> = metricsIndex.value[submetric.name] || {};
          return {
            key: `submetric_${submetric.name}`,
            name: submetric.name,
            ...subMetricMeta,
            searchString: [
              (subMetricMeta.title || '').toLowerCase(),
              (subMetricMeta.description || '').toLowerCase(),
            ].join(' '),
          };
        }),
      };
    }),
  }));
});

const metricsList = computed(() => columnGroup.value[0]?.items || []);

const entityList = computed(() => {
  const result: Array<{ type: string; key: string; entity: any }> = [];
  columnGroup.value.forEach((group: any) => {
    result.push({
      type: 'group',
      key: `group_${group.group_name}`,
      entity: group,
    });

    group.items.forEach((metric: any) => {
      result.push({
        type: 'metric',
        key: `metric_${metric.name}`,
        entity: metric,
      });

      metric.items.forEach((submetric: any) => {
        result.push({
          type: 'submetric',
          key: `submetric_${submetric.name}`,
          entity: submetric,
        });
      });
    });
  });

  return result;
});
const isHiddenEntityIndex = computed(() => {
  const result: Record<string, boolean> = {};

  entityList.value
    .filter((item) => item.type === 'submetric')
    .forEach((item) => {
      result[item.key] = item.entity.searchString.indexOf(pSearch.value) < 0;
    });

  entityList.value
    .filter((item) => item.type === 'metric')
    .forEach((item) => {
      const isEverySubItemHidden = item.entity.items.every((subItem: any) => result[`submetric_${subItem.name}`]);
      result[item.key] = item.entity.searchString.indexOf(pSearch.value) < 0 && isEverySubItemHidden;
    });

  entityList.value
    .filter((item) => item.type === 'group')
    .forEach((item) => {
      const isEverySubItemHidden = item.entity.items.every((subItem: any) => result[`metric_${subItem.name}`]);
      result[item.key] = item.entity.searchString.indexOf(pSearch.value) < 0 && isEverySubItemHidden;
    });

  return result;
});
//#endregion

//#region methods
const open = async () => {
  isVisible.value = true;
  isLoading.value = true;

  metricsList.value.forEach((item: CrmMetricItem) => {
    selectedMetricsIndex.value[item.column_name] = props.selectedMetrics.includes(item.column_name);
  });

  await nextTick();
  isLoading.value = false;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
};

const handleSave = () => {
  emit('select', selectedMetricsList.value);
  handleClose();
};
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="crm-activity-metrics-popup"
    :title="$t('crm.activities.actions.action_popup.add_metrics_title')"
    :width="500"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="search"
          :placeholder="$t('reports.colunms_settings.placeholder_search')"
          autosize
          is-search
        />
        <UiScrollbar
          wrap-style="max-height: 250px; min-height: 250px;"
          :native="true"
        >
          <template
            v-for="(metric, index) in metricsList"
            :key="metric.key"
          >
            <CardItem
              v-if="!isHiddenEntityIndex[metric.key]"
              :no-border="Number(index) === 0"
              no-value
            >
              <template v-slot:title>
                <UiCheckbox
                  v-model="selectedMetricsIndex[metric.name]"
                  :label="metric.title"
                />
                <div class="crm-activity-metrics-popup__item-description">
                  {{ metric.description }}
                </div>
              </template>
            </CardItem>
          </template>
        </UiScrollbar>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save_metrics') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.crm-activity-metrics-popup{
  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__item-description {
    font-size: 13px;
    width: 45%;
  }
}
</style>
